<template>
  <div class="col-md-12 col-lg-10 m-auto">
    <div class="card mb-3">
      <div class="card-body p-2 pl-3 d-flex align-items-center">
        <h5 class="m-0"> {{ $t('edit_printable') }} </h5>
        <button class="btn btn-info ml-auto" @click="savePrintable(false)">{{ $t('save') }}</button>
        <button class="btn btn-primary ml-2" @click="savePrintable(true)">{{ $t('save_close') }}</button>
      </div>
    </div>

    <card>
      <ul slot="header" class="nav nav-tabs card-header-tabs">
        <li class="nav-item">
          <a id="nav-home-tab" class="nav-link active" data-toggle="tab" href="#nav-home" role="tab" aria-controls="nav-home">Basdata</a>
        </li>
        <li v-if="!creating" class="nav-item">
          <a id="nav-components-tab" class="nav-link" data-toggle="tab" href="#nav-components" role="tab" aria-controls="nav-components">Komponenter</a>
        </li>
      </ul>
      <form class="tab-content" @submit.prevent="savePrintable" @keydown="form.onKeydown($event)">
        <div id="nav-home" class="tab-pane fade py-3 show active" role="tabpanel" aria-labelledby="nav-home-tab">
          <div class="row">
            <!-- Name -->
            <div class="form-group col-md-6 col-lg-8">
              <label>{{ $t('name') }}</label>
              <input v-model="form.name"
                     :class="{ 'is-invalid': form.errors.has('name') }"
                     class="form-control"
                     type="text"
                     name="name">
              <has-error :form="form"
                         field="name" />
            </div>

            <!-- Company -->
            <div class="form-group col-md-6 col-lg-4">
              <label>{{ $t('company') }}</label>
              <select v-model="company" :class="{ 'is-invalid': form.errors.has('company_id') }" name="company_id" class="form-control" @change="fetchCompanyData">
                <option value="">Välj företag</option>
                <option v-for="company in companies" :key="company.id" :value="company.id">{{ company.name }}</option>
              </select>
              <has-error :form="form"
                         field="company_id" />
            </div>

            <!-- PrintableTypes -->
            <div class="form-group col-md-6 col-lg-3">
              <label>{{ $t('printabletype') }}</label>
              <select v-model="form.printable_type_id" :class="{ 'is-invalid': form.errors.has('printable_type_id') }" name="printable_type_id" class="form-control">
                <option value="">Välj trycksakstyp</option>
                <option v-for="printable_type in printabletypes" :key="printable_type.id" :value="printable_type.id">{{ printable_type.name }}</option>
              </select>
              <has-error :form="form"
                         field="printable_type_id" />
            </div>

            <!-- Project -->
            <div class="form-group col-md-6 col-lg-3">
              <label>{{ $t('projects') }}</label>
              <select v-model="form.project_id" :disabled="!company" :class="{ 'is-invalid': form.errors.has('project_id') }" name="project_id" class="form-control">
                <option value="">Välj projekt</option>
                <option v-for="project in projects" :key="project.id" :value="project.id">{{ project.name }}</option>
              </select>
              <has-error :form="form"
                         field="project_id" />
            </div>

            <!-- PrintablePrinter -->
            <div class="form-group col-md-6 col-lg-3">
              <label>{{ $t('printable_printer') }}</label>
              <select v-model="form.printer" :class="{ 'is-invalid': form.errors.has('printable_printer') }" name="printable_printer" class="form-control">
                <option value="">Välj Tryckeri</option>
                <option v-for="printable_printer in printable_printer" :key="printable_printer.name" :value="printable_printer.name">{{ printable_printer.name }}</option>
              </select>
              <has-error :form="form"
                         field="printable_printer" />
            </div>

            <!-- Deadline -->
            <div class="form-group col-md-6 col-lg-3">
              <label>{{ $t('deadline') }}</label>
              <div class="input-group">
                <div class="input-group-prepend">
                  <span class="input-group-text">
                    <fa :icon="['fal','calendar-alt']" />
                  </span>
                </div>
                <date-picker v-model="form.deadline" :config="dpConfig" :class="{ 'is-invalid': form.errors.has('deadline') }" />
              </div>
              <has-error :form="form"
                         field="deadline" />
            </div>

            <!-- Auditor -->
            <div class="form-group col-md-6 col-lg-4">
              <label>{{ $t('auditor') }}</label>
              <select v-model="form.auditor_id" :disabled="!company" :class="{ 'is-invalid': form.errors.has('auditor_id') }" name="auditor_id" class="form-control">
                <option value="">Välj granskare</option>
                <option v-for="user in internal_users" :key="user.id" :value="user.id">{{ user.name }} ({{ user.company.name }})</option>
              </select>
              <has-error :form="form" field="auditor_id" />
            </div>

            <!-- Client manager -->
            <div class="form-group col-md-6 col-lg-4">
              <label>{{ $t('client_manager') }}</label>
              <select v-model="form.client_manager_id" :disabled="!company" :class="{ 'is-invalid': form.errors.has('client_manager_id') }" name="client_manager_id" class="form-control">
                <option value="">Välj kundansvarig</option>
                <option v-for="user in external_users" :key="user.id" :value="user.id">{{ user.name }} ({{ user.company.name }})</option>
              </select>
              <has-error :form="form"
                         field="client_manager_id" />
            </div>

            <!-- Final notice -->
            <div class="form-group col-md-6 col-lg-4">
              <label>{{ $t('final_notice_recipient') }}</label>
              <select v-model="form.final_notice_id" :disabled="!company" :class="{ 'is-invalid': form.errors.has('final_notice_id') }" name="final_notice_id" class="form-control">
                <option value="">Välj mottagare av slutnotis</option>
                <option v-for="user in external_users" :key="user.id" :value="user.id">{{ user.name }} ({{ user.company.name }})</option>
              </select>
              <has-error :form="form" field="final_notice_id" />
            </div>

            <!-- Client auditor -->
            <div class="form-group col-md-6 col-lg-8">
              <label>{{ $t('client_auditor') }}</label>
              <!--<select v-model="form.client_auditor_id" :disabled="!company" :class="{ 'is-invalid': form.errors.has('client_auditor_id') }" name="client_auditor_id" class="form-control">
                                <option value="">Välj kundgranskare</option>
                                <option v-for="user in external_users" :key="user.id" :value="user.id">{{ user.name }} ({{ user.company.name }})</option>
                            </select>-->
              <!--<v-select multiple v-model="form.client_auditors" label="name" value="id" :options="external_users"></v-select>-->
              <Select2 v-model="form.client_auditors" :options="external_users" :settings="{ multiple: true, theme: 'classic'}" />

              <has-error :form="form"
                         field="client_auditor_id" />
            </div>

            <!-- Copies -->
            <div class="form-group col-md-6 col-lg-4">
              <label>{{ $t('edition') }}</label>
              <input v-model="form.copies" :class="{ 'is-invalid': form.errors.has('copies') }" type="text" class="form-control" >
              <has-error :form="form"
                         field="copies" />
            </div>

            <div class="form-group col-md-12 col-lg-12">
              <label>{{ $t('description') }}</label>
              <textarea v-model="form.description" :class="{ 'is-invalid': form.errors.has('description') }" class="form-control" />
            </div>

            <!-- Cut bleed -->
            <!--<div class="form-group col-md-6 col-lg-4 pt-4">
                            <span class="switch">
                                <input v-model="form.cuts_bleed"
                                        :class="{ 'is-invalid': form.errors.has('active') }"
                                        class="switch"
                                        type="checkbox"
                                        name="cuts_bleed"
                                        id="cuts_bleed">
                                <label class="form-check-label" for="cuts_bleed">
                                    {{ $t('cuts_bleed') }}?
                                </label>
                            </span>
                        </div>-->
          </div>
        </div>
        <div id="nav-components" class="tab-pane fade" role="tabpanel" aria-labelledby="nav-components-tab">
          <div class="row">
            <div class="col-3 mt-0 border-right py-3">
              <h5>Kategorier</h5>
              <ul class="nav categories flex-column">
                <category-item-article v-for="(category) in categories" :key="category.id" :category="category" :company="company" :is-sidebar="false" />
              </ul>
            </div>
            <div class="col-4 py-3 border-right">
              <h5>Komponenter i kategori</h5>
              <ul class="list-unstyled">
                <li v-for="component in components" :key="component.id">
                  <div class="custom-control custom-checkbox">
                    <input :id="'checkbox' + component.id"
                           v-model="form.components"
                           :value="component.id"
                           type="checkbox"
                           class="custom-control-input"
                           @change="toggleInPrintable(component)">

                    <label :for="'checkbox' + component.id" class="custom-control-label">{{ component.attachment.filename }}</label>
                  </div>
                </li>
              </ul>
            </div>
            <div class="col-5 py-3">
              <h5>Valda komponenter</h5>
              <ul class="list-unstyled">
                <li v-for="component in printable.components" :key="component.id">
                  <span class="text-danger mr-2" @click="removeFromPrintable(component)">
                    <fa :icon="['fas','trash-alt']" />
                  </span>

                  <span class="text-muted">{{ component.category.name }}</span> /
                  {{ component.attachment.filename }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </form>
    </card>
  </div>
</template>
<script>
import $ from 'jquery'
import axios from 'axios'
import Form from 'vform'
import CategoryItemArticle from '~/components/CategoryItemArticle'

export default {
  middleware: ['auth', 'admin'],
  components: {
    CategoryItemArticle
  },
  data () {
    return {
      loading: false,
      printable: {},
      printables: null,
      creating: false,
      categories: [],
      company: '',
      companies: [],
      components: [],
      projects: [],
      printabletypes: [],
      printable_printer: [
        {
          name: 'Mixiprint'
        },
        {
          name: 'Mediapoolprint'
        }
      ],
      users: [],
      error: null,
      form: new Form({
        name: '',
        deadline: new Date(),
        auditor_id: '',
        deadline: '',
        client_auditors: [],
        client_manager_id: '',
        printable_type_id: '',
        copies: '',
        project_id: '',
        printer: '',
        company_id: '',
        cuts_bleed: 0,
        components: [],
        description: '',
        final_notice_id: ''
      }),
      dpConfig: {
        format: 'YYYY-MM-DD',
        useCurrent: false,
        locale: 'sv'
      }
    }
  },
  computed: {
    id () {
      return this.$route.params.id ? this.$route.params.id : false
    },
    internal_users () {
      return this.users.filter(user => user.role >= 3)
    },
    external_users () {
      return this.users.filter(user => user.role < 3)
    }
  },
  watch: {
    company (newCompany, oldCompany) {
      this.form.company_id = newCompany.id
    },
    $route (to, from) {
      if (to.name == 'printables.create' || from.name == 'printables.create') {
        this.$router.go()
      }

      if (from.name == 'printables.id.edit' && to.name == 'printables.id.edit' && to.query.category_id) {
        this.fetchComponents(to.query.category_id)
      }
    }
  },
  created () {
    if (this.id) {
      this.fetchPrintable(this.id)

      if (this.$route.query.category_id) {
        this.fetchComponents(this.$route.query.category_id)
      }
    } else {
      this.creating = true
    }

    this.fetchCompanies()
    this.fetchCategories()
  },
  methods: {
    groupBy (xs, key) {
      return xs.reduce(function (rv, x) {
        (rv[x[key]] = rv[x[key]] || []).push(x)
        return rv
      }, {})
    },
    populateForm (data) {
      for (var key in data) {
        if (this.form.hasOwnProperty(key)) {
          this.form[key] = data[key]
        }
      }

      this.company = data.company_id
      this.form.company_id = data.company_id
    },
    removeFromPrintable (component) {
      let printable_components = this.printable.components.findIndex(el => el.id == component.id)
      this.printable.components.splice(printable_components, 1)

      let checked_components = this.form.components.findIndex(el => el == component.id)
      this.form.components.splice(checked_components, 1)
    },
    toggleInPrintable (component) {
      let foundAt = this.printable.components.findIndex(el => el.id == component.id)

      if (foundAt > -1) {
        this.printable.components.splice(foundAt)
      } else {
        this.printable.components.push(component)
      }
    },
    async fetchCategories () {
      let company = this.$store.getters['auth/company'].id

      const { data } = await axios.get('/api/sidebar/' + company + '?section=categories')

      this.categories = data
    },
    async fetchData (id) {
      this.fetchPrintable(id)
    },
    async fetchComponents (category_id) {
      const { data } = await axios.get('/api/component?category_id=' + category_id)

      this.components = data.sort(function (a, b) {
        return a.attachment.filename.localeCompare(b.attachment.filename)
      })
    },
    async fetchPrintable (id) {
      if (id) {
        const { data } = await axios.get('/api/printable/' + id)
        this.printable = data
        this.populateForm(data)
        this.form.components = data.components.map(a => a.id)
        this.form.client_auditors = data.client_auditors.map(a => a.user.id)

        this.fetchCompanyData()
      }
    },
    async fetchCompanies () {
      const { data } = await axios.get('/api/company')
      this.companies = data
    },
    async fetchCompanyData () {
      this.fetchUsers()
      this.fetchProjects()
      this.fetchPrintableTypes()
    },
    async fetchUsers () {
      if (this.company) {
        var query = '?include_admins=true&company_id=' + this.company

        const { data } = await axios.get('/api/user' + query)

        let new_data = data
          .filter(user => user.role > 1)
          .map(user => {
            user.text = user.name
            return user
          })

        this.users = new_data
      }
    },
    async fetchProjects () {
      if (this.company) {
        var query = '?company_id=' + this.company

        const { data } = await axios.get('/api/project' + query)
        this.projects = data
      }
    },
    async fetchPrintableTypes () {
      if (this.company) {
        var query = '?company_id=' + this.company

        const { data } = await axios.get('/api/printabletype' + query)
        this.printabletypes = data
      }
    },
    async savePrintable (routeback) {
      this.form.company_id = this.company
      var method = this.id ? 'patch' : 'post'
      var url = '/api/printable' + (this.id ? '/' + this.id : '')

      const { data } = await this.form[method](url)

      if (routeback) {
        let lastPath = this.$store.getters['router/lastroute']
        this.$router.push(lastPath)
      } else {
        this.$router.push({ path: '/printables/' + data.id + '/edit' })
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// resources/assets/js/pages/printables/form.vue